import React from 'react';
import { Box, Typography, Chip, Tooltip } from '@mui/material';
import {
    Pool as PoolIcon,
    Token as TokenIcon,
    AccountBalance as NFTIcon
} from '@mui/icons-material';
import { LPTokenInfo } from '../../types/DashboardTypes';

interface LPTokenCardProps {
    lpTokens: LPTokenInfo[];
    variant?: 'default' | 'compact';
}

const LPTokenCard: React.FC<LPTokenCardProps> = ({
    lpTokens,
    variant = 'default'
}) => {
    if (!lpTokens || lpTokens.length === 0) return null;

    const fungibleTokens = lpTokens.filter(lp => lp.type === 'fungible');
    const nftTokens = lpTokens.filter(lp => lp.type === 'nft');

    const getDEXColor = (dex: string) => {
        switch (dex) {
            case 'uniswap': return '#FF007A';
            case 'aerodrome': return '#0052FF';
            case 'sushiswap': return '#FA52A0';
            default: return '#666';
        }
    };

    const getDEXIcon = (dex: string, type: string) => {
        if (type === 'nft') return <NFTIcon fontSize="small" />;
        return <PoolIcon fontSize="small" />;
    };

    if (variant === 'compact') {
        return (
            <Box sx={{ mt: 1, p: 1, bgcolor: 'rgba(212, 175, 55, 0.08)', borderRadius: 1 }}>
                <Typography variant="caption" fontWeight="bold" color="primary">
                    🏊 LP: {fungibleTokens.length} | 🎨 NFTs: {nftTokens.length}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 1, p: 1, bgcolor: 'rgba(212, 175, 55, 0.08)', borderRadius: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                🏊 Liquidity Positions ({lpTokens.length})
            </Typography>

            {fungibleTokens.map((lp, idx) => (
                <Box key={`fungible-${idx}`} sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 0.5
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getDEXIcon(lp.dex, lp.type)}
                        <Typography variant="body2">
                            {lp.displayName}
                        </Typography>
                        <Chip
                            label={lp.dex.toUpperCase()}
                            size="small"
                            sx={{
                                height: 16,
                                fontSize: '0.6rem',
                                bgcolor: getDEXColor(lp.dex),
                                color: 'white'
                            }}
                        />
                    </Box>
                    <Typography variant="body2" fontWeight="bold">
                        {parseFloat(lp.balance).toFixed(4)} LP
                    </Typography>
                </Box>
            ))}

            {nftTokens.map((nft, idx) => (
                <Box key={`nft-${idx}`} sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 0.5
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getDEXIcon(nft.dex, nft.type)}
                        <Typography variant="body2">
                            {nft.displayName}
                        </Typography>
                        <Chip
                            label={nft.dex.toUpperCase()}
                            size="small"
                            sx={{
                                height: 16,
                                fontSize: '0.6rem',
                                bgcolor: getDEXColor(nft.dex),
                                color: 'white'
                            }}
                        />
                    </Box>
                    <Tooltip title={`Token ID: ${nft.tokenId}`}>
                        <Typography variant="body2" fontWeight="bold" color="secondary">
                            NFT #{nft.tokenId}
                        </Typography>
                    </Tooltip>
                </Box>
            ))}
        </Box>
    );
};

export default LPTokenCard;
